import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import {
  PROFILE__RESET,
  PROFILE__MUTATE_LOADING_STATUS,
  PROFILE__UPDATE_BASIC_INFO,
  PROFILE__UPDATE_PAYMENT,
  PROFILE__UPDATE_PASSWORD,
  PROFILE__CONFIRM_EMAIL,
  PROFILE__UPDATE_NOTIFICATION_PREFERENCE,
  AUTH__MUTATE_CUSTOMER,
  AUTH__MUTATE_USER,
  PROPERTIES__MUTATE_PROPERTIES,
  AUTH__UPDATE_TOKEN,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';

const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [PROFILE__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [PROFILE__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [PROFILE__UPDATE_BASIC_INFO](
    { commit, rootState },
    {
      fname = undefined,
      lname = undefined,
      email = undefined,
      phone = undefined,
      share_contact_details = undefined,
    }
  ) {
    commit(PROFILE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { user_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'PUT',
        route: `/users/${user_id}`,
        payload: {
          fname,
          lname,
          email,
          phone,
          share_contact_details,
        },
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_USER, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROFILE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [PROFILE__UPDATE_PAYMENT]({ commit, rootState }, { stripe, cardNumber, onSuccess = _.noop }) {
    commit(PROFILE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return stripe
      .createToken(cardNumber)
      .then(({ token, error }) => {
        if (error) {
          return Promise.reject(error);
        }

        if (_.get(token, 'id')) {
          return lsApiService.request({
            method: 'PUT',
            route: `/customers/${customer_id}?include=properties`,
            payload: {
              card_token: token.id,
            },
          });
        }

        return Promise.reject('Stripe response missing token');
      })
      .then(({ data }) => {
        const { balance, last4, card_expiration_date, has_payment_failure } = data;
        commit(AUTH__MUTATE_CUSTOMER, {
          balance,
          last4,
          card_expiration_date,
          has_payment_failure,
        });
        if (_.get(data, 'properties')) {
          commit(PROPERTIES__MUTATE_PROPERTIES, data.properties);
        }
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROFILE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [PROFILE__UPDATE_PASSWORD](
    { commit, rootState, dispatch },
    { password_new, onSuccess = _.noop }
  ) {
    commit(PROFILE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { user_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'PUT',
        route: `/users/${user_id}`,
        payload: { password_new },
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_USER, data);
        if (data.authtoken) {
          dispatch(AUTH__UPDATE_TOKEN, data.authtoken.id);
        }
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROFILE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [PROFILE__CONFIRM_EMAIL]({ commit, rootState }) {
    commit(PROFILE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { user_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/users/${user_id}/unbounce`,
      })
      .then(() => {
        commit(AUTH__MUTATE_USER, { email_bounced: false });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROFILE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [PROFILE__UPDATE_NOTIFICATION_PREFERENCE]({ commit, rootState }, { settings }) {
    commit(PROFILE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'PUT',
        route: `/customers/${customer_id}/settings`,
        payload: settings,
      })
      .then(({ data }) => {
        commit(AUTH__MUTATE_CUSTOMER, { settings: data });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROFILE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {};

export const profile = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
